import { useEffect, useState } from 'react'
import { api, type AccessRole } from '../lib/api.ts'
import {
  label,
  PERMISSION_HINT,
  PERMISSION_KIND,
  PERMISSION_KIND_LABEL,
  PERMISSION_LABEL,
  PermissionKind,
} from '../lib/vocabulary.ts'
import { Button } from '../components/Button.tsx'
import { Checkbox } from '../components/Checkbox.tsx'
import { EntityForm, Field, FieldGroup } from '../components/EntityForm.tsx'
import { icons } from '../components/icons.ts'
import { useConfirm } from '../components/Confirm.tsx'
import { useToast } from '../components/Toast.tsx'

/** The permission that lifts "only their own orçamentos"; `Permission.SeeAllUsers` in @rp/core. */
const SEE_ALL_USERS = 'see_all_users'

type Member = { user_id: string; email: string; name: string | null }

/**
 * One função, or a new one (#280, ADR 0024).
 *
 * The pharmacy renames it and chooses its permissions. Creating and deleting
 * are a super admin's, so `canManage` decides whether "Apagar" is drawn; a
 * new role is only ever reached from a button only they see. Deleting is
 * refused while anybody holds the role -- the server says so too -- because
 * the alternative is a colleague whose queue empties mid-shift.
 */
export function Role({
  id,
  canManage,
  onBack,
  onCreated,
}: {
  /** Absent for a new role. */
  id: string | undefined
  canManage: boolean
  onBack: () => void
  onCreated: (id: string) => void
}) {
  const [role, setRole] = useState<AccessRole | undefined>()
  const [members, setMembers] = useState<Member[]>([])
  const [permissions, setPermissions] = useState<string[]>([])
  const [name, setName] = useState('')
  const [held, setHeld] = useState<string[]>([])
  const [error, setError] = useState<string | undefined>()
  const [saving, setSaving] = useState(false)
  const toast = useToast()
  const confirm = useConfirm()

  function take(next: AccessRole) {
    setRole(next)
    setName(next.name)
    setHeld(next.permissions)
  }

  useEffect(() => {
    if (id === undefined) {
      void api
        .roles()
        .then((r) => setPermissions(r.permissions))
        .catch((cause: unknown) =>
          setError(cause instanceof Error ? cause.message : 'falha ao carregar'),
        )
      return
    }
    void api
      .role(id)
      .then((r) => {
        take(r.role)
        setMembers(r.members)
        setPermissions(r.permissions)
      })
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'falha ao carregar'),
      )
  }, [id])

  const creating = id === undefined
  const dirty = creating
    ? name.trim() !== ''
    : role !== undefined &&
      (name.trim() !== role.name ||
        held.length !== role.permissions.length ||
        held.some((one) => !role.permissions.includes(one)))

  async function save() {
    if (name.trim() === '') return
    setSaving(true)
    try {
      if (creating) {
        const r = await api.createRole({ name: name.trim(), permissions: held })
        toast.show('Função criada', { tone: 'success' })
        onCreated(r.role.id)
      } else {
        const r = await api.updateRole(id, { name: name.trim(), permissions: held })
        take(r.role)
        toast.show('Função salva', { tone: 'success' })
      }
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao salvar', { tone: 'error' })
    } finally {
      setSaving(false)
    }
  }

  async function remove() {
    if (role === undefined) return
    const sure = await confirm({
      title: `Apagar a função "${role.name}"?`,
      message: 'Não dá para desfazer.',
      confirmLabel: 'Apagar',
      tone: 'danger',
    })
    if (!sure) return
    try {
      await api.deleteRole(role.id)
      toast.show('Função apagada', { tone: 'success' })
      onBack()
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao apagar', { tone: 'error' })
    }
  }

  const loaded = creating ? permissions.length > 0 : role !== undefined
  const fixed = role?.system === true
  const holds = (kind: PermissionKind) => held.some((one) => PERMISSION_KIND[one] === kind)
  const sees = {
    missing: !holds(PermissionKind.Stage)
      ? 'etapa'
      : !holds(PermissionKind.State)
        ? 'estado'
        : undefined,
  }

  return (
    <div className="flex flex-col gap-5 p-5 pt-0">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={onBack}
        >
          Funções
        </Button>
        <h1 className="text-tH4 text-text-strong">
          {creating ? 'Nova função' : (role?.name ?? 'Função')}
        </h1>
        {canManage && role !== undefined && !role.system && (
          <Button
            className="ml-auto"
            variant="secondary"
            tone="danger"
            size="small"
            leadingIcon={icons.remove}
            disabled={members.length > 0}
            // Said before the click, not after the refusal.
            title={
              members.length > 0 ? 'Troque a função de quem a tem antes de apagá-la' : undefined
            }
            onClick={() => void remove()}
          >
            Apagar
          </Button>
        )}
      </div>

      {error !== undefined && <p className="text-tP text-text-error">{error}</p>}

      {!loaded ? (
        error === undefined && <p className="text-tP text-text-weak">Carregando…</p>
      ) : (
        <EntityForm
          // The system role is shown, never edited (ADR 0024): no save.
          {...(fixed
            ? {
                description:
                  'Função do sistema: tem todas as permissões, inclusive as que vierem a existir, e não se altera nem se apaga. Ninguém tira esta função de si mesmo.',
              }
            : { onSave: () => void save() })}
          saving={saving}
          dirty={dirty}
          saveLabel={creating ? 'Criar' : 'Salvar'}
        >
          <Field label="Nome" value={name} editable={!fixed} onChange={setName} wide />
          <FieldGroup
            label="Permissões"
            helperText="Quem tem esta função vê uma conferência quando vê a etapa em que ela está e também o estado dela."
          >
            <div className="flex flex-col gap-4">
              {Object.values(PermissionKind).map((kind) => (
                <div key={kind} className="flex flex-col gap-3">
                  <span className="text-tSmall text-text-weak uppercase tracking-wide">
                    {PERMISSION_KIND_LABEL[kind]}
                  </span>
                  {permissions
                    .filter((permission) => PERMISSION_KIND[permission] === kind)
                    .map((permission) => (
                      <label key={permission} className="flex items-start gap-3">
                        <Checkbox
                          checked={fixed || held.includes(permission)}
                          disabled={saving || fixed}
                          onChange={(e) =>
                            setHeld((current) =>
                              e.target.checked
                                ? [...current, permission]
                                : current.filter((one) => one !== permission),
                            )
                          }
                        />
                        <span className="flex flex-col">
                          <span className="text-tP text-text-strong">
                            {label(PERMISSION_LABEL, permission)}
                          </span>
                          <span className="text-tSmall text-text-weak">
                            {label(PERMISSION_HINT, permission)}
                          </span>
                        </span>
                      </label>
                    ))}
                </div>
              ))}
              {/* Both are required, so a role missing either sees nothing --
                  said while ticking, not discovered by somebody's empty queue. */}
              {sees.missing !== undefined && (
                <p className="text-tSmall text-text-error">
                  Sem nenhuma {sees.missing}, quem tem esta função não vê nenhuma conferência.
                </p>
              )}
              {sees.missing === undefined && !held.includes(SEE_ALL_USERS) && (
                // Not an error -- the orçamentista works this way on purpose --
                // but the reason a person with this role sees nothing, when
                // their Formula Certa user is not filled in.
                <p className="text-tSmall text-text-warning">
                  Sem “{label(PERMISSION_LABEL, SEE_ALL_USERS)}”, cada pessoa vê só os orçamentos
                  que ela mesma digitou, e precisa ter o usuário do Fórmula Certa preenchido em
                  Usuários.
                </p>
              )}
            </div>
          </FieldGroup>
          {!creating && (
            <FieldGroup label={`Usuários com esta função (${members.length})`}>
              {members.length === 0 ? (
                <span className="text-tSmall text-text-weak">Ninguém.</span>
              ) : (
                <ul className="text-tP text-text-strong flex flex-col gap-1">
                  {members.map((member) => (
                    <li key={member.user_id}>
                      {member.name ?? member.email}
                      {member.name !== null && (
                        <span className="text-text-weak"> · {member.email}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </FieldGroup>
          )}
        </EntityForm>
      )}
    </div>
  )
}
